import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Calendar,
  Users,
  Heart,
  Star,
  ArrowRight,
  PawPrint
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const HouseCleaningPage = () => {
  const cleaningPlans = [
    {
      frequency: "Weekly",
      description: "Perfect for busy families who want their home consistently fresh",
      features: [
        "Same day, same time each week",
        "Your trusted team knows your home",
        "Priority scheduling",
        "Flexible holiday adjustments"
      ]
    },
    {
      frequency: "Bi-Weekly",
      description: "Our most popular option for maintaining a clean, healthy home",
      features: [
        "Every other week consistency",
        "Predictable schedule",
        "Same cleaners every visit",
        "Balance of quality and value"
      ],
      popular: true
    },
    {
      frequency: "Monthly",
      description: "Regular maintenance for homes that stay tidy between visits",
      features: [
        "Once per month deep refresh",
        "Tackle areas you miss",
        "Same reliable team",
        "Perfect supplement to your routine"
      ]
    }
  ];

  const whatWeClean = [
    "All rooms dusted and vacuumed",
    "Kitchen surfaces cleaned and sanitized",
    "Bathrooms scrubbed and disinfected",
    "Floors mopped and cared for",
    "Trash removed and bins relined",
    "Beds made (if requested)",
    "General tidying and straightening"
  ];

  const faqs = [
    {
      question: "Will I have the same cleaners each time?",
      answer: "Yes. This is one of the biggest differences between Pierce Premier and larger companies. Bob and Heather personally oversee your service, and you'll see the same trusted faces every visit. No rotating crews, no strangers in your home."
    },
    {
      question: "What if I have pets?",
      answer: "We love pets! Many of our recurring clients have dogs, cats, and other animals. Just let us know about your pets when we schedule, and we'll work around their routines and needs."
    },
    {
      question: "Can I customize what gets cleaned?",
      answer: "Absolutely. During your first visit, we'll discuss your priorities and preferences. Some clients want certain rooms focused on more than others, or specific tasks added. We're flexible and here to serve your needs."
    },
    {
      question: "What happens if I need to reschedule?",
      answer: "Life happens—we get it. Just give us a call or text, and we'll work with you to find a new time. As a small, local business, we're easier to reach and more flexible than big companies."
    },
    {
      question: "Do you bring your own supplies?",
      answer: "Yes, we arrive fully equipped with everything needed. If you have specific products you prefer or sensitivities to certain cleaners, we're happy to use what you provide."
    }
  ];

  // Structured Data
  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services | Pierce Premier Cleaning",
      description: "Recurring house cleaning in Brevard County with the same trusted team every visit. Locally owned by Bob & Heather. Weekly, bi-weekly, and monthly plans available.",
      url: "/services/house-cleaning"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Services", url: "/services" },
      { name: "House Cleaning" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Service",
      description: "Regular house cleaning service with consistent team members. Weekly, bi-weekly, and monthly recurring plans available throughout Brevard County."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services | Pierce Premier | Brevard County, FL"
        description="Recurring house cleaning in Brevard County. Same trusted cleaners every visit. Weekly, bi-weekly & monthly plans. Locally owned by Bob & Heather. Pet-friendly. Call 321-408-1347."
        canonical="/services/house-cleaning"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-cyan-600 text-white mb-4">Most Popular Service</Badge>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Your home deserves more than just any cleaning service—it deserves people you trust. At Pierce Premier, you&apos;ll work with the same team every visit. No rotating crews, no strangers, just Bob, Heather, and their trusted cleaners who get to know your home.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  With Heather&apos;s 15+ years of executive housekeeping experience and our commitment to long-term relationships, we&apos;re here to make your life easier—one visit at a time.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-cyan-600 text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=800&q=80" 
                  alt="Professional house cleaning service in Brevard County"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border-4 border-cyan-100 hidden lg:block">
                  <div className="flex items-center gap-3">
                    <Users className="w-8 h-8 text-cyan-600" />
                    <div>
                      <div className="font-bold text-lg text-gray-900">Same Team</div>
                      <div className="text-sm text-gray-600">Every Visit</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Pierce Premier for Recurring Service */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                The Pierce Premier Difference
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                We&apos;re a locally owned, husband-and-wife business. That means you get personal service, direct communication, and a team that actually cares about your home.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Trusted Cleaners, Always</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Unlike franchise operations that rotate crews, Pierce Premier assigns the same cleaners to your home every time. They learn your preferences, know your space, and become familiar faces you can trust.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Locally Owned by Bob & Heather</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We live and work in Palm Bay. When you call, you reach us—not a national call center. We personally oversee every job and stand behind our work because our reputation matters in this community.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">15+ Years of Professional Experience</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather brings over 15 years of executive housekeeping experience to every home we serve. This isn&apos;t just surface cleaning—it&apos;s attention to detail that comes from years in hospitality-level service.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <PawPrint className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Pet-Friendly & Flexible</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We love working in homes with pets! Many of our recurring clients have dogs, cats, and other animals. We&apos;ll work around feeding times, nap schedules, and any special needs your furry family members have.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Cleaning Plans */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Choose Your Cleaning Schedule
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Whether you need weekly maintenance or monthly refreshes, we&apos;ll create a schedule that works for your family.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {cleaningPlans.map((plan, idx) => (
                <Card key={idx} className={`border-2 relative ${plan.popular ? 'border-cyan-500 shadow-xl' : ''}`}>
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-cyan-600 text-white px-4 py-1">Most Popular</Badge>
                    </div>
                  )}
                  <CardContent className="p-8 text-center">
                    <Calendar className="w-12 h-12 text-cyan-600 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{plan.frequency}</h3>
                    <p className="text-gray-600 mb-6">{plan.description}</p>
                    <ul className="space-y-3 text-left">
                      {plan.features.map((feature, featureIdx) => (
                        <li key={featureIdx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                What&apos;s Included in Every Visit?
              </h2>
              <p className="text-base sm:text-lg text-gray-600">
                Your recurring service keeps your home fresh and healthy. Here&apos;s what we cover during each visit:
              </p>
            </div>

            <Card className="border-2">
              <CardContent className="p-8">
                <div className="grid sm:grid-cols-2 gap-4">
                  {whatWeClean.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-cyan-50 rounded-lg border border-cyan-100">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Note:</span> Recurring cleaning maintains your home between visits. For a reset or first-time service, we recommend starting with a <Link to="/services/deep-cleaning" className="text-cyan-600 font-semibold hover:text-cyan-700">deep cleaning</Link>, then moving to a recurring schedule.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions About Recurring Cleaning
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">More questions?</p>
              <Link to="/faq" className="text-cyan-600 font-semibold hover:text-cyan-700">
                View Full FAQ →
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-4 border-cyan-200 shadow-xl">
              <CardContent className="p-8 md:p-12 text-center">
                <Star className="w-12 h-12 text-yellow-500 fill-yellow-500 mx-auto mb-4" />
                <blockquote className="text-xl md:text-2xl text-gray-700 font-medium mb-6 italic">
                  &quot;Best deep cleaning service in Brevard County! They got stains out I thought were permanent. Amazing attention to detail.&quot;
                </blockquote>
                <p className="text-gray-600 font-semibold">David Chen</p>
                <p className="text-gray-500 text-sm">Viera, FL</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Serving Brevard County Families
            </h2>
            <p className="text-gray-600 mb-8">
              From Palm Bay to the beaches, we bring consistent, trusted cleaning to homes across Brevard County.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Palm Bay", "Melbourne", "Satellite Beach", "Indialantic", 
                "Indian Harbour Beach", "Cocoa Beach", "Merritt Island", 
                "Viera", "Rockledge", "West Melbourne"
              ].map((city, idx) => (
                <Badge key={idx} variant="outline" className="text-sm py-2 px-4">
                  {city}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title="Ready for a Cleaner Home?"
          description="Let's talk about a cleaning schedule that works for you. Same trusted cleaners, flexible plans, and the Pierce Premier commitment to quality."
          primaryButtonText="Request Free Quote"
        />
      </div>
    </>
  );
};

export default HouseCleaningPage;
